// 프로필 편집 시트 — 회원이 자기 프로필을 직접 편집.
//
// 성별, 레벨(성별에 따라 옵션 다름), 주 사용 손, 백핸드, 선호 코트(복수),
// 연락처. 성별 변경 시에는 진행 중인 경기에 영향이 갈 수 있어 confirm으로
// 한 번 더 확인 — 그 외 필드는 부담 없이 수정 가능.
//
// 호출자(myRecord 등)가 onSaved 콜백을 주면 저장 후 호출 — 보통 view를
// 다시 그리는 용도.

import { useEffect, useRef, useState } from "preact/hooks";
import { api } from "../api";
import { levelOptionsForGender } from "../auth";
import { invalidateMembersCache } from "../state";
import { attachPhoneFormatter, SURFACE_OPTIONS } from "../ui/format";
import { mountPreact } from "../ui/preact-mount";
import { closeSheet, openSheet, toast, withBusy } from "../ui/sheet";

const DOMINANT_HAND_OPTIONS: Array<[string, string]> = [
  ["right", "오른손"],
  ["left", "왼손"],
];
const BACKHAND_STYLE_OPTIONS: Array<[string, string]> = [
  ["two", "투핸드"],
  ["one", "원핸드"],
];
const GENDER_OPTIONS: Array<[string, string]> = [
  ["male", "남"],
  ["female", "여"],
];

interface ChipRowProps {
  options: Array<[string, string]>;
  selected: string | null;
  onPick: (value: string) => void;
}

function ChipRow({ options, selected, onPick }: ChipRowProps) {
  return (
    <div class="chip-row">
      {options.map(([v, lbl]) => (
        <button
          key={v}
          type="button"
          class={"chip" + (selected === v ? " chip--selected" : "")}
          onClick={() => onPick(v)}
        >
          {lbl}
        </button>
      ))}
    </div>
  );
}

function ProfileEditForm({
  member,
  onSaved,
}: {
  member: any;
  onSaved?: () => void;
}) {
  const originalGender = member.gender || null;
  const [genderVal, setGenderVal] = useState<string | null>(originalGender);
  const [levelVal, setLevelVal] = useState<string | null>(member.level_category || null);
  const [handVal, setHandVal] = useState<string | null>(member.dominant_hand || null);
  const [backhandVal, setBackhandVal] = useState<string | null>(member.backhand_style || null);
  const [surfaces, setSurfaces] = useState<Set<string>>(() => new Set(member.preferred_surfaces || []));

  const contactRef = useRef<HTMLInputElement>(null);
  const submitRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (contactRef.current) attachPhoneFormatter(contactRef.current);
  }, []);

  const levelOptions = levelOptionsForGender(genderVal);

  function onGenderPick(v: string) {
    if (genderVal === v) return;
    if (!levelOptionsForGender(v).includes(levelVal)) setLevelVal(null);
    setGenderVal(v);
  }

  function onLevelPick(v: string) {
    setLevelVal(levelVal === v ? null : v);
  }

  function onHandPick(v: string) {
    setHandVal(handVal === v ? null : v);
  }

  function onBackhandPick(v: string) {
    setBackhandVal(backhandVal === v ? null : v);
  }

  function onSurfacePick(v: string) {
    setSurfaces((prev) => {
      const next = new Set(prev);
      if (next.has(v)) next.delete(v);
      else next.add(v);
      return next;
    });
  }

  async function onSubmit(e: Event) {
    e.preventDefault();
    if (genderVal && genderVal !== originalGender) {
      if (!confirm("성별을 바꾸면 참여 중인 경기 대진이 영향받을 수 있어요. 계속할까요?")) return;
    }
    const body: any = {
      level_category: levelVal || null,
      dominant_hand: handVal || null,
      backhand_style: backhandVal || null,
      preferred_surfaces: Array.from(surfaces),
      phone_or_contact: contactRef.current?.value.trim() || null,
    };
    if (genderVal && genderVal !== originalGender) body.gender = genderVal;
    try {
      await withBusy(submitRef.current, "저장 중…", async () => {
        await api(`/api/v1/members/${member.member_id}`, { method: "PATCH", body });
        invalidateMembersCache();
        toast("프로필을 저장했어요");
        closeSheet();
        if (onSaved) onSaved();
      });
    } catch (err: any) { toast(err.message); }
  }

  return (
    <form class="form" onSubmit={onSubmit}>
      <div class="field">
        <span class="field__label">성별</span>
        <ChipRow options={GENDER_OPTIONS} selected={genderVal} onPick={onGenderPick} />
      </div>
      {levelOptions.length > 0 && (
        <div class="field">
          <span class="field__label">레벨</span>
          <div class="chip-row">
            {levelOptions.map((opt) => (
              <button
                key={opt}
                type="button"
                class={"chip" + (opt === levelVal ? " chip--selected" : "")}
                onClick={() => onLevelPick(opt)}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>
      )}
      <div class="field">
        <span class="field__label">주 사용 손</span>
        <ChipRow options={DOMINANT_HAND_OPTIONS} selected={handVal} onPick={onHandPick} />
      </div>
      <div class="field">
        <span class="field__label">백핸드</span>
        <ChipRow options={BACKHAND_STYLE_OPTIONS} selected={backhandVal} onPick={onBackhandPick} />
      </div>
      <div class="field">
        <span class="field__label">선호 코트 (복수 선택 가능)</span>
        <div class="chip-row">
          {SURFACE_OPTIONS.map(([v, lbl]) => (
            <button
              key={v}
              type="button"
              class={"chip" + (surfaces.has(v) ? " chip--selected" : "")}
              onClick={() => onSurfacePick(v)}
            >
              {lbl}
            </button>
          ))}
        </div>
      </div>
      <div class="field">
        <span class="field__label">연락처 (선택)</span>
        <input
          ref={contactRef}
          class="input"
          type="text"
          maxLength={40}
          placeholder="숫자만 입력해도 하이픈 자동 (또는 카카오톡 ID)"
          value={member.phone_or_contact || ""}
        />
      </div>
      <button ref={submitRef} class="btn btn--primary btn--block" type="submit">저장</button>
    </form>
  );
}

export function openProfileEditSheet(member: any, onSaved?: () => void): void {
  const form = mountPreact(<ProfileEditForm member={member} onSaved={onSaved} />);
  openSheet("프로필 편집", form);
}
